import { Injectable, computed, effect, signal } from '@angular/core';
import { ApiService } from './api.service';
import { UserFile, UserProfile } from './models';
import { SessionService } from './session.service';

export interface ProfileCompletionItem {
  key: 'avatar' | 'profile' | 'cv' | 'mfa';
  labelKey: string;
  route: string;
  completed: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProfileCompletionService {
  private readonly files = signal<UserFile[]>([]);
  private readonly isMfaEnabled = signal(false);
  private loadedForUsername = '';

  readonly items = computed<ProfileCompletionItem[]>(() => {
    const user = this.session.currentUser();
    const files = this.files();
    const isMfaEnabled = this.isMfaEnabled();

    return [
      { key: 'avatar', labelKey: 'home.completionSteps.avatar', route: '/account', completed: this.hasProfilePicture(user) },
      { key: 'profile', labelKey: 'home.completionSteps.profile', route: '/account', completed: this.hasProfileReadinessContent(user) },
      { key: 'cv', labelKey: 'home.completionSteps.cv', route: '/files', completed: files.some(file => file.type === 'CV') },
      { key: 'mfa', labelKey: 'home.completionSteps.mfa', route: '/security', completed: isMfaEnabled }
    ];
  });

  readonly completedCount = computed(() => this.items().filter(item => item.completed).length);

  readonly score = computed(() => {
    const user = this.session.currentUser();
    if (!user) return 0;

    const files = this.files();
    return Math.round(
      (this.hasProfileReadinessContent(user) ? 40 : 0)
      + (this.hasProfilePicture(user) ? 20 : 0)
      + (files.some(file => file.type === 'CV') ? 25 : 0)
      + (this.isMfaEnabled() ? 15 : 0)
    );
  });

  readonly barWidth = computed(() => this.session.isLoggedIn() ? Math.max(8, this.score()) : 0);

  constructor(private api: ApiService, private session: SessionService) {
    effect(() => {
      const user = this.session.currentUser();
      if (!user) {
        this.loadedForUsername = '';
        this.files.set([]);
        this.isMfaEnabled.set(false);
        return;
      }

      if (this.loadedForUsername === user.username) return;
      this.loadedForUsername = user.username;
      void this.loadAccountSignals();
    });
  }

  async refresh(): Promise<void> {
    if (!this.session.currentUser()) return;
    await this.loadAccountSignals();
  }

  private async loadAccountSignals(): Promise<void> {
    const [filesResult, mfaResult] = await Promise.allSettled([
      this.api.listFiles(),
      this.api.getSoftwareMfaStatus()
    ]);

    this.files.set(filesResult.status === 'fulfilled' ? filesResult.value : []);
    this.isMfaEnabled.set(mfaResult.status === 'fulfilled' ? mfaResult.value.enabled : false);
  }

  private hasProfilePicture(user: UserProfile | null): boolean {
    return Boolean(user?.avatar?.trim()) && user?.avatarStatus !== 'PROCESSING';
  }

  private hasProfileSummary(user: UserProfile | null): boolean {
    return Boolean(user?.profileSummary?.trim());
  }

  private hasProfileReadinessContent(user: UserProfile | null): boolean {
    return this.hasProfileSummary(user) || this.hasProfileWizardAnswers(user);
  }

  private hasProfileWizardAnswers(user: UserProfile | null): boolean {
    if (!user?.profileWizard) return false;

    const meaningfulFields = [
      user.profileWizard.diplomas,
      user.profileWizard.favoriteTopics,
      user.profileWizard.dislikedTasks,
      user.profileWizard.methods,
      user.profileWizard.values,
      user.profileWizard.targetEnvironment,
      user.profileWizard.achievements,
      user.profileWizard.motivation
    ].filter(value => Boolean(value?.trim()));

    return meaningfulFields.length >= 3;
  }
}